/**
 * The identities a service's messages may come from — SMS sender IDs.
 *
 * Sync is the usual way in, where the provider can list them: it brings each
 * identity and whether it is approved. Adding one by hand is for an identity the
 * provider has not listed yet, and it starts as pending until a sync confirms it.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Star, Trash2 } from 'lucide-react';
import {
  Alert,
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  Input,
  QueryPending,
  api,
  formatDateTime,
  inSentence,
  toast,
  type Tone,
} from '@connect/web-shared';

import {
  messagingKeys,
  type ChannelDef,
  type ClientChannel,
  type Sender,
  type SenderSyncResult,
} from './types';

const STATUS_TONES: Record<string, Tone> = {
  approved: 'success',
  pending: 'warning',
  rejected: 'danger',
  unknown: 'neutral',
};

export function ChannelSendersCard({ channel, def }: { channel: ClientChannel; def: ChannelDef }) {
  const queryClient = useQueryClient();
  const [newSender, setNewSender] = useState('');
  const [removing, setRemoving] = useState<Sender | null>(null);
  const canSync = channel.capabilities.includes('sync_senders');
  const noun = def.sender_label;
  const isSms = channel.channel === 'sms';

  const senders = useQuery({
    queryKey: messagingKeys.senders(channel.public_id),
    queryFn: () => api.get<Sender[]>(`/messaging/channels/${channel.public_id}/senders`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: messagingKeys.senders(channel.public_id) });
    // The service card counts them.
    void queryClient.invalidateQueries({
      queryKey: messagingKeys.channels(channel.client_public_id),
    });
  };

  const add = useMutation({
    mutationFn: () =>
      api.post<Sender>(`/messaging/channels/${channel.public_id}/senders`, {
        sender_id: newSender,
      }),
    onSuccess: () => {
      setNewSender('');
      invalidate();
    },
  });

  const sync = useMutation({
    mutationFn: () =>
      api.post<SenderSyncResult>(`/messaging/channels/${channel.public_id}/senders/sync`),
    onSuccess: (result) => {
      queryClient.setQueryData(messagingKeys.senders(channel.public_id), result.senders);
      invalidate();
      toast.success(
        `Synced from ${channel.provider_label}`,
        `${result.created} new, ${result.updated} changed` +
          (result.not_at_provider.length
            ? ` · not listed by the provider: ${result.not_at_provider.join(', ')}`
            : ''),
      );
    },
  });

  const makeDefault = useMutation({
    mutationFn: (sender: Sender) =>
      api.patch(`/messaging/senders/${sender.public_id}`, { is_default: true }),
    onSuccess: invalidate,
    onError: (error) =>
      toast.error('Could not change that', error instanceof ApiError ? error.message : undefined),
  });

  const error = add.error ?? sync.error;

  return (
    <Card>
      <CardHeader
        title={`${noun}s`}
        actions={
          canSync ? (
            <Can permission="messaging.sender.create">
              <Button
                variant="secondary"
                size="sm"
                loading={sync.isPending}
                onClick={() => sync.mutate()}
              >
                <RefreshCw className="size-3.5" />
                Sync from {channel.provider_label}
              </Button>
            </Can>
          ) : null
        }
      />
      <CardBody>
        <div className="space-y-3">
          {error ? (
            <Alert tone="danger" title="Could not complete that">
              {error instanceof ApiError ? error.message : 'Could not reach the server.'}
            </Alert>
          ) : null}

          {senders.data === undefined ? (
            <QueryPending query={senders} label={`Loading ${inSentence(noun)}s`} />
          ) : !senders.data.length ? (
            <p className="text-sm text-muted">
              No {inSentence(noun)}s yet.
              {canSync ? ` Sync them from ${channel.provider_label}, or add one below.` : ' Add one below.'}
            </p>
          ) : (
            <ul className="divide-y divide-line">
              {senders.data.map((sender) => (
                <li
                  key={sender.public_id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2 first:pt-0"
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    {sender.label ? (
                      // A WhatsApp number is sent from by its id and known by its display.
                      <>
                        <span className="text-sm text-heading">{sender.label}</span>
                        <span className="font-mono text-2xs text-faint">{sender.sender_id}</span>
                      </>
                    ) : (
                      <span className="font-mono text-sm text-heading">{sender.sender_id}</span>
                    )}
                    <Badge tone={STATUS_TONES[sender.status] ?? 'neutral'}>{sender.status}</Badge>
                    {sender.is_default ? (
                      <Badge tone="info">
                        <Star className="size-2.5" />
                        Default
                      </Badge>
                    ) : null}
                    <span className="text-2xs text-faint">
                      {sender.synced_at
                        ? `synced ${formatDateTime(sender.synced_at)}`
                        : 'added by hand'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {sender.is_default ? null : (
                      <Can permission="messaging.sender.edit">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => makeDefault.mutate(sender)}
                        >
                          Make default
                        </Button>
                      </Can>
                    )}
                    <Can permission="messaging.sender.delete">
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label={`Remove ${sender.sender_id}`}
                        onClick={() => setRemoving(sender)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </Can>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Can permission="messaging.sender.create">
            <form
              className="flex items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (newSender.trim()) add.mutate();
              }}
            >
              <Input
                label={`Add a ${inSentence(noun)} by hand`}
                value={newSender}
                onChange={(event) =>
                  setNewSender(isSms ? event.target.value.toUpperCase() : event.target.value)
                }
                placeholder={isSms ? 'INGLTD' : undefined}
                maxLength={isSms ? 11 : 64}
                containerClassName="flex-1"
                spellCheck={false}
              />
              <Button type="submit" size="sm" loading={add.isPending} disabled={!newSender.trim()}>
                Add
              </Button>
            </form>
          </Can>
        </div>
      </CardBody>

      <Confirm
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={`Remove this ${inSentence(noun)}?`}
        message={`${removing?.sender_id} will no longer be offered for this service's messages. It stays at the provider${canSync ? '; a sync brings it back' : ''}.`}
        confirmLabel="Remove"
        onConfirm={async () => {
          if (!removing) return;
          try {
            await api.delete(`/messaging/senders/${removing.public_id}`);
            invalidate();
          } catch (caught) {
            toast.error('Could not remove it', caught instanceof ApiError ? caught.message : undefined);
          }
        }}
      />
    </Card>
  );
}
